import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  Box
} from '@mui/material';
import LocalHospital from '@mui/icons-material/LocalHospital';
import { API_BASE_URL } from '../lib/constants';
import theme from '../theme/theme';
import { ThemeProvider } from '@mui/material/styles';
import DraftSaveButton from '../components/DraftSaveButton';
import { DRAFT_KEYS, saveDraft, loadDraft } from '../utils/draftUtils';


import { useNotification } from '../contexts/NotificationContext';

const AddInventory = () => {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    product_name: '',
    hospital_id: 1,
    quantity: '',
    price: '',
  });

  const [editItem, setEditItem] = useState(null);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validate = () => {
    const tempErrors = {};
    if (!formData.product_name.trim()) {
      tempErrors.product_name = 'Product name is required';
    } else if (formData.product_name.trim().length < 2) {
      tempErrors.product_name = 'Product name must be at least 2 characters long';
    }

    if (!formData.quantity) {
      tempErrors.quantity = 'Quantity is required';
    } else if (isNaN(formData.quantity) || formData.quantity <= 0) {
      tempErrors.quantity = 'Quantity must be a positive number';
    } else if (formData.quantity > 999) {
      tempErrors.quantity = 'Quantity cannot exceed 999';
    }

    if (!formData.price) {
      tempErrors.price = 'Price is required';
    } else if (isNaN(formData.price) || formData.price <= 0) {
      tempErrors.price = 'Price must be a positive number';
    } else if (formData.price > 999999) {
      tempErrors.price = 'Price cannot exceed 999,999';
    }

    setErrors(tempErrors);
    const hasError = Object.keys(tempErrors).length > 0;
    if (hasError) {
      showNotification('error', `Required Field: ${Object.values(tempErrors)[0]}`);
    }
    return !hasError;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "quantity" && value.length > 3) return;
    if (name === "price" && value.length > 6) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = "";
    switch (name) {
      case 'product_name':
        if (!value.trim()) error = 'Product name is required';
        else if (value.trim().length < 2) error = 'Product name must be at least 2 characters long';
        break;
      case 'quantity':
        if (!value) error = 'Quantity is required';
        else if (value <= 0) error = 'Quantity must be positive';
        else if (value > 999) error = 'Quantity cannot exceed 999';
        break;
      case 'price':
        if (!value) error = 'Price is required';
        else if (value <= 0) error = 'Price must be positive';
        else if (value > 999999) error = 'Price cannot exceed 999,999';
        break;
      default: break;
    }
    if (error) setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      if (editItem) {
        await axios.put(`${API_BASE_URL}/inventory/${editItem.id}`, formData);
        showNotification('success', `Item "${formData.product_name}" updated successfully!`);
      } else {
        await axios.post(`${API_BASE_URL}/add_inventory`, formData);
        showNotification('success', `New item "${formData.product_name}" added to inventory!`);
      }
      setTimeout(() => navigate('/home', { state: { activeMenu: 'Inventory' } }), 1000);
    } catch (error) {
      console.error('Error saving inventory:', error);
      const msg = error.response?.data?.error || 'Failed to save inventory item. Check server connection.';
      showNotification('error', msg);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <button
            onClick={() => navigate('/home', { state: { activeMenu: 'Inventory' } })}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#008080',
              fontSize: '16px',
              fontWeight: 500,
              cursor: 'pointer',
              marginRight: '16px',
            }}
          >
            ← Back
          </button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            {/* Heading with Icon */}
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >
              INVENTORY MANAGEMENT

            </Typography>

            {/* Right-aligned Logo and Text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>
          {/* Save Draft Button */}
          {/* <DraftSaveButton
          formKey={DRAFT_KEYS.ADD_INVENTORY}
          uhidNo={formData.product_name || 'default'}
          formData={formData}
          onDraftLoaded={handleDraftLoaded}
        /> */}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={<span>Product Name <span style={{ color: 'red' }}>*</span></span>}
                  name="product_name"
                  value={formData.product_name}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.product_name}
                  helperText={errors.product_name}
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={<span>Quantity <span style={{ color: 'red' }}>*</span></span>}
                  name="quantity"
                  type="number"
                  inputProps={{ max: 999 }}
                  value={formData.quantity}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.quantity}
                  helperText={errors.quantity}
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={<span>Price <span style={{ color: 'red' }}>*</span></span>}
                  name="price"
                  inputProps={{ max: 999999 }}
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.price}
                  helperText={errors.price}
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid item xs={12}>
                <Button type="submit" variant="contained" color="primary" fullWidth>
                  {editItem ? 'Update Item' : 'Add Item'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default AddInventory;


